import React, { useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteBlocksRequest, getListBlocksRequest } from '../../../services/onlinestore/blockServiceRequest';
import { Link, useNavigate } from 'react-router-dom';
import { AppSelect } from '../../../constants/appSelect';
import { newObjectKeys } from '../../../utils/utility';

const { Text } = Typography;

const QuanLyKhoi = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataBlocks,
    isPending,
    refetch: refetchDataBlocks,
  } = useQuery({ queryKey: ['dataBlocks', params], queryFn: () => getListBlocksRequest(params) });

  const { mutate: mutateDeleteBlock } = useMutation({
    mutationFn: deleteBlocksRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Xóa khối thành công!',
        });
        refetchDataBlocks();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.title) {
      Object.assign(temp, { title: values?.title });
    }
    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    }

    setParams(temp);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Tên khối',
      dataIndex: ['id', 'title'],
      key: 'id',
      width: 180,
      render: (_, { title, id }) => (
        <Link to={`/online-store/quan-ly-khoi/${id}/sua-khoi`} className="text-[#1677FF]">
          {title}
        </Link>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Đường link',
      dataIndex: 'url',
      key: 'url',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'order_place',
      key: 'order_place',
      width: 80,
    },
    {
      title: 'Loại giao diện',
      dataIndex: 'default_display',
      key: 'default_display',
      width: 140,
      render: (default_display) => (
        <Text className="text-[#667085]">
          {AppSelect.UI_TYPE?.find((item) => item?.value === default_display)?.label}
        </Text>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag className="px-2 py-1" color={AppSelect.STATUS?.find((item) => item?.value === status)?.color}>
          {AppSelect.STATUS?.find((item) => item?.value === status)?.label}
        </Tag>
      ),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: ['updated_by', 'updated_at'],
      key: 'updated_at',
      width: 180,
      render: (_, { updated_by, updated_at }) => (
        <Space direction="vertical">
          {updated_by && <Text className="text-[#667085]">{updated_by}</Text>}
          {updated_at && <Text className="text-[#667085]">{updated_at}</Text>}
        </Space>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`/online-store/quan-ly-khoi/${record?.id}/sua-khoi`)}>
            <EditOutlined />
          </Button>
          <Button type="text" onClick={() => mutateDeleteBlock({ id: record?.id })}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách khối</h1>
        <Link to={'/online-store/quan-ly-khoi/them-khoi'}>
          <Button type="primary">
            <PlusOutlined />
            Thêm khối
          </Button>
        </Link>
      </div>

      <Form
        name="search_blocks"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="title" label={<Text>Tên khối</Text>}>
              <Input placeholder="Nhập tên khối" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...AppSelect.STATUS]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params && Object.values(newObjectKeys(params, ['title', 'status'])).some((value) => value) && (
              <Button
                htmlType="button"
                onClick={() => {
                  formSearch.resetFields();
                  setParams({ page: 1, limit: params.limit });
                }}
              >
                <DeleteOutlined />
                Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataBlocks?.data}
        totalItem={dataBlocks?.total}
        params={params}
        setParams={setParams}
        scroll={{ x: 1260 }}
      />
    </div>
  );
};

export default QuanLyKhoi;
